import { API_V1 } from '../config'
import { apiFetch } from './api'

export interface SignatureRequestSummary {
  id: string
  koblink_id: string
  invited_email: string
  invitee_name: string
  status: 'waiting' | 'pending' | 'signed' | 'rejected' | 'expired' | 'cancelled'
  level: 'SES' | 'AES' | 'QES'
  signing_step: number | null
  signed_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  last_reminder_sent: string | null
  reminder_count: number
}

export interface DocumentTypeInfo {
  id: number
  name: string
  icon: string
  color: string
}

export interface DepartmentInfo {
  id: number
  name: string
  color: string
}

export interface ActorInfo {
  koblink_id: string
  display_name: string
}

export interface Document {
  id: string
  koblink_id: string
  title: string
  status: DocumentStatus
  original_file_type: string | null
  signer_count: number
  signed_count: number
  created_at: string
  updated_at: string
  folder_id: number | null
  folder_name: string | null
  tags: { id: number; name: string; color: string }[]
  tag_ids: number[]
  document_type: DocumentTypeInfo | null
  department: DepartmentInfo | null
  contract_value: string | null
  contract_currency: string | null
  contract_expires_at: string | null
  counterparties: string[] | null
  responsible_person: ActorInfo | null
  parent_document: { koblink_id: string; title: string } | null
  relation_type: 'amendment' | 'renewal' | 'related' | null
  replaced_by: string | null
}

export interface DocumentTreeNode {
  koblink_id: string
  title: string
  completed_at: string | null
  relation_type: 'amendment' | 'renewal' | 'related' | null
  is_current: boolean
  children: DocumentTreeNode[]
}

export interface DocumentDetail extends Document {
  signature_requests: SignatureRequestSummary[]
  owner: string
  completed_at: string | null
}

export type DocumentStatus =
  | 'draft'
  | 'negotiating'
  | 'pending'
  | 'partially_signed'
  | 'completed'
  | 'declined'
  | 'recalled'
  | 'expired'

export interface DocumentListResponse {
  results: Document[]
  total_count: number
  page: number
  page_size: number
  total_pages: number
}

export interface Signer {
  name: string
  email: string
}

export interface AddSignersRequest {
  signing_mode: 'parallel' | 'sequential'
  document_level: string
  signers: Array<{
    name: string
    email: string
    step?: number
    field: {
      page: number
      x: number
      y: number
      width: number
      height: number
    }
  }>
}

export interface AddSignersResponse {
  success: boolean
  koblink_id: string
  signers_added: number
}

export interface SendInvitationsResponse {
  success: boolean
  invitations_sent: number
  message: string
}

/** Fetch recent documents */
export async function getDocuments(params?: {
  page?: number
  page_size?: number
  owner?: string
  folder_id?: number | null
  tags?: number[]
  document_type_id?: number | null
  department_id?: number | null
  status?: string | null
}): Promise<DocumentListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  searchParams.set('page_size', String(params?.page_size ?? 10))
  if (params?.owner) searchParams.set('actor_id', params.owner)
  if (params?.folder_id != null) searchParams.set('folder_id', String(params.folder_id))
  if (params?.tags && params.tags.length > 0) searchParams.set('tags', params.tags.join(','))
  if (params?.document_type_id != null) searchParams.set('document_type_id', String(params.document_type_id))
  if (params?.department_id != null) searchParams.set('department_id', String(params.department_id))
  if (params?.status) searchParams.set('status', params.status)

  const query = searchParams.toString()
  const response = await apiFetch(`${API_V1}/documents/?${query}`)

  if (!response.ok) {
    throw new Error(`Failed to fetch documents: ${response.status}`)
  }
  return response.json()
}

/** Fetch a single document with full details (including signature requests) */
export async function getDocument(id: string, actorId?: string): Promise<DocumentDetail> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(id)}/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(id)}/`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.status}`)
  }
  return response.json()
}

/** Upload a DOCX file from Word */
export async function uploadDocument(
  file: Blob,
  filename: string,
  title: string,
  ownerId?: string,
): Promise<Document> {
  const formData = new FormData()
  formData.append('file', file, filename)
  formData.append('title', title)
  if (ownerId) {
    formData.append('owner_id', ownerId)
  }

  const response = await apiFetch(`${API_V1}/documents/upload/`, {
    method: 'POST',
    body: formData,
    // Don't set Content-Type — browser sets it with boundary for multipart
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || errorData?.message || `Upload failed: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Add signers to a draft document */
export async function addSigners(
  koblinkId: string,
  data: AddSignersRequest,
  actorId?: string,
): Promise<AddSignersResponse> {
  const url = actorId
    ? `${API_V1}/documents/${koblinkId}/add-signers/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${koblinkId}/add-signers/`
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to add signers: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Send signing invitations (starts the signing process) */
export async function sendInvitations(
  koblinkId: string,
  message?: string,
  actorId?: string,
): Promise<SendInvitationsResponse> {
  const url = actorId
    ? `${API_V1}/documents/${koblinkId}/send/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${koblinkId}/send/`
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(message ? { message } : {}),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const msg = errorData?.error || `Failed to send invitations: ${response.status}`
    throw new Error(msg)
  }
  return response.json()
}

/** Recall a document (cancel signing) */
export async function recallDocument(documentId: string, actorId?: string): Promise<void> {
  const params = actorId ? `?actor_id=${encodeURIComponent(actorId)}` : ''
  const response = await apiFetch(`${API_V1}/documents/${documentId}/recall/${params}`, {
    method: 'POST',
  })
  if (!response.ok) {
    throw new Error(`Failed to recall document: ${response.status}`)
  }
}

/** Download a document (signed PDF if available, otherwise original) */
export async function downloadDocument(koblinkId: string): Promise<Blob> {
  const response = await apiFetch(`${API_V1}/documents/${koblinkId}/download/`, {
    method: 'GET',
  })
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`)
  }
  return response.blob()
}

/** Update document metadata (folder, type, department, etc.) */
export async function updateDocumentMetadata(
  koblinkId: string,
  data: Record<string, unknown>,
  actorId?: string,
): Promise<Document> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/`
  const response = await apiFetch(url, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to update document: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}

/** Get document tree (parent and children hierarchy) */
export async function getDocumentTree(
  koblinkId: string,
  actorId?: string,
): Promise<DocumentTreeNode> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/tree/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/tree/`
  const response = await apiFetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch document tree: ${response.status}`)
  }
  return response.json()
}

/** Link a document as child of another (set parent + relation type) */
export async function relateDocument(
  koblinkId: string,
  parentKoblinkId: string,
  relationType: 'amendment' | 'renewal' | 'related',
  actorId?: string,
): Promise<Document> {
  return updateDocumentMetadata(
    koblinkId,
    { parent_document: parentKoblinkId, relation_type: relationType },
    actorId,
  )
}

/** Remove parent relationship from a document */
export async function unrelateDocument(
  koblinkId: string,
  actorId?: string,
): Promise<Document> {
  return updateDocumentMetadata(
    koblinkId,
    { parent_document: null, relation_type: null },
    actorId,
  )
}

/** Create a renewal of an existing document */
export async function renewDocument(
  koblinkId: string,
  data?: { title?: string },
  actorId?: string,
): Promise<Document> {
  const url = actorId
    ? `${API_V1}/documents/${encodeURIComponent(koblinkId)}/renew/?actor_id=${encodeURIComponent(actorId)}`
    : `${API_V1}/documents/${encodeURIComponent(koblinkId)}/renew/`
  const response = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => null)
    const message = errorData?.error || `Failed to renew document: ${response.status}`
    throw new Error(message)
  }
  return response.json()
}
